import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card, Form, Input, Button, DatePicker, Space, Typography, Row, Col,
  message, Spin, Tag, Modal
} from 'antd';
import {
  ArrowLeftOutlined, SaveOutlined, ClockCircleOutlined, PictureOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { articlesAPI } from '../../utils/api';
import BulkMediaUploader from '../../components/BulkMediaUploader';
import './CaminoEditor.css';

const { Title, Text } = Typography;

const SECTION_GALERIA = 'Camino — Galería';
const BUNNY_FOLDER = 'camino-2026';

const computeStatus = (dateObj, isDraft) => {
  if (isDraft) return 'draft';
  if (dateObj && dayjs(dateObj).isAfter(dayjs())) return 'scheduled';
  return 'published';
};

const GaleriaEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [media, setMedia] = useState([]);
  const [uploaderUploading, setUploaderUploading] = useState(false);

  // Cargar galería existente
  const loadGaleria = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await articlesAPI.getArticleById(id);
      if (!res.success) throw new Error(res.error || 'No se pudo cargar la galería');
      const a = res.data;
      if (a.section !== SECTION_GALERIA) {
        message.warning('Esta entrada no es una Galería del Camino.');
        navigate('/camino');
        return;
      }
      form.setFieldsValue({
        title: a.title || '',
        date: a.date ? dayjs(a.date) : dayjs()
      });
      setMedia(Array.isArray(a.media) ? a.media : []);
    } catch (error) {
      console.error('Error cargando galería:', error);
      message.error('Error al cargar la galería');
      navigate('/camino');
    } finally {
      setLoading(false);
    }
  }, [id, isNew, form, navigate]);

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ date: dayjs() });
    } else {
      loadGaleria();
    }
  }, [isNew, loadGaleria, form]);

  // Detectar si hay uploads en curso (best-effort, mirando el media que devuelve el uploader).
  // El uploader solo emite items con status='done'. Pero podríamos perder los uploading
  // si el usuario pulsa guardar mientras suben. Para eso, exponemos el conteo desde el uploader.
  const onUploaderChange = useCallback((items) => {
    setMedia(items);
  }, []);

  const handleSubmit = async (isDraft) => {
    if (uploaderUploading) {
      message.warning('Esperá a que terminen las subidas pendientes.');
      return;
    }
    try {
      const values = await form.validateFields();
      setSaving(true);
      const dateIso = values.date ? values.date.toISOString() : new Date().toISOString();

      // Una galería puede tener título corto opcional. Si está vacío, usamos
      // un placeholder con la fecha para mantener el campo title (que es NOT NULL).
      const titleFinal = (values.title || '').trim()
        || `Galería del ${dayjs(dateIso).format('DD/MM/YYYY')}`;

      const payload = {
        title: titleFinal,
        subtitle: null,
        content: '',
        section: SECTION_GALERIA,
        date: dateIso,
        status: computeStatus(values.date, isDraft),
        imageUrl: media[0]?.type === 'image' ? media[0].url : null, // miniatura por defecto
        hasAudio: false,
        audioUrl: null,
        hasVideo: media.some(m => m.type === 'video'),
        videoUrl: null,
        videoType: null,
        videoThumbnail: null,
        media: media
      };

      const res = isNew
        ? await articlesAPI.createArticle(payload)
        : await articlesAPI.updateArticle(id, payload);

      if (!res.success) throw new Error(res.error || 'Error al guardar');

      const where = payload.status === 'scheduled'
        ? `programada para ${dayjs(dateIso).format('DD/MM/YYYY HH:mm')}`
        : payload.status === 'draft' ? 'guardada como borrador' : 'publicada';
      message.success(`Galería ${where}`);
      navigate('/camino');
    } catch (error) {
      if (error?.errorFields) return;
      console.error('Error guardando galería:', error);
      message.error(error?.response?.data?.error || 'Error al guardar la galería');
    } finally {
      setSaving(false);
    }
  };

  const previewDate = Form.useWatch('date', form);
  const statusTag = useMemo(() => {
    if (!previewDate) return null;
    if (dayjs(previewDate).isAfter(dayjs())) {
      return <Tag color="orange" icon={<ClockCircleOutlined />}>Se programará</Tag>;
    }
    return <Tag color="green">Se publicará inmediatamente</Tag>;
  }, [previewDate]);

  if (loading) {
    return <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  return (
    <div className="camino-editor" style={{ padding: 24, maxWidth: 1180, margin: '0 auto' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/camino')}>
              Volver
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              {isNew ? 'Nueva Galería' : 'Editar Galería'}
            </Title>
          </Space>
          <div>
            <Text type="secondary">Sección: {SECTION_GALERIA}</Text>
          </div>
        </Col>
        <Col>{statusTag}</Col>
      </Row>

      <Form form={form} layout="vertical">
        <Card title="Datos de la galería" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={14}>
              <Form.Item
                name="title"
                label="Título (opcional)"
                extra="Si lo dejas vacío, se usará la fecha del día como título."
              >
                <Input placeholder='Ej.: "Día 3 — Sarria"' maxLength={140} showCount />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                name="date"
                label="Fecha de publicación"
                rules={[{ required: true, message: 'Indica la fecha' }]}
              >
                <DatePicker
                  showTime={{ format: 'HH:mm' }}
                  format="DD/MM/YYYY HH:mm"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title={<><PictureOutlined /> Fotos y videos</>} style={{ marginBottom: 16 }}>
          <BulkMediaUploader
            value={media}
            onChange={onUploaderChange}
            folder={BUNNY_FOLDER}
          />
          <UploaderTracker
            onUploadingChange={setUploaderUploading}
            mediaCount={media.length}
          />
        </Card>

        <Card>
          <Space>
            <Button
              icon={<SaveOutlined />}
              onClick={() => handleSubmit(true)}
              loading={saving}
              disabled={uploaderUploading}
            >
              Guardar borrador
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => handleSubmit(false)}
              loading={saving}
              disabled={uploaderUploading}
            >
              {previewDate && dayjs(previewDate).isAfter(dayjs())
                ? 'Programar'
                : 'Publicar ahora'}
            </Button>
            <Button onClick={() => {
              Modal.confirm({
                title: '¿Descartar cambios?',
                content: 'Volverás al listado sin guardar.',
                okText: 'Descartar', cancelText: 'Seguir editando',
                okButtonProps: { danger: true },
                onOk: () => navigate('/camino')
              });
            }}>Cancelar</Button>
            {uploaderUploading && (
              <Text type="warning">Hay subidas en curso…</Text>
            )}
          </Space>
        </Card>
      </Form>
    </div>
  );
};

// Helper invisible que detecta si hay uploads en curso mirando los nodos del DOM
// del uploader. Es un hack ligero pero evita acoplar BulkMediaUploader.
// Si la galería tiene N items en el output (onChange) y el DOM tiene M cards,
// si M > N significa que hay (M - N) en estado uploading/error.
const UploaderTracker = ({ mediaCount, onUploadingChange }) => {
  useEffect(() => {
    let cancelled = false;
    const check = () => {
      if (cancelled) return;
      const cards = document.querySelectorAll('.ant-progress');
      onUploadingChange?.(cards.length > 0);
    };
    const t = setInterval(check, 500);
    check();
    return () => { cancelled = true; clearInterval(t); };
  }, [mediaCount, onUploadingChange]);
  return null;
};

export default GaleriaEditor;
